import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';
import type {
  AgentFunctionDto,
  AgentFunctionDetailDto,
  AgentFunctionRequestDto,
  AgentFunctionRequestDetailDto,
  AgentFunctionFilters,
  AgentFunctionRequestFilters,
  CreateAgentFunctionRequest,
  UpdateAgentFunctionRequest,
} from '../types/agentFunction';

/**
 * Service for managing agent functions, execution, and request history.
 */
export class AgentFunctionService extends BaseService {
  // ─── CRUD ──────────────────────────────────────────────────────────────

  async getAgentFunctions(filters?: AgentFunctionFilters): Promise<PagedResult<AgentFunctionDto>> {
    const params = new URLSearchParams();
    if (filters?.pageNumber) params.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);

    const url = `/agent-functions${params.toString() ? `?${params.toString()}` : ''}`;
    return this.get<PagedResult<AgentFunctionDto>>(url);
  }

  async getAgentFunction(id: string): Promise<AgentFunctionDetailDto> {
    return this.get<AgentFunctionDetailDto>(`/agent-functions/${id}`);
  }

  async getAgentFunctionBySlug(slug: string): Promise<AgentFunctionDetailDto> {
    return this.get<AgentFunctionDetailDto>(`/agent-functions/slug/${encodeURIComponent(slug)}`);
  }

  async createAgentFunction(data: CreateAgentFunctionRequest): Promise<AgentFunctionDetailDto> {
    return this.post<AgentFunctionDetailDto>('/agent-functions', {
      ...data,
      isActive: data.isActive ?? true,
      inputSchemaJson: data.inputSchemaJson ?? '{}',
      outputSchemaJson: data.outputSchemaJson ?? '{}',
    });
  }

  async updateAgentFunction(id: string, data: UpdateAgentFunctionRequest): Promise<AgentFunctionDetailDto> {
    return this.put<AgentFunctionDetailDto>(`/agent-functions/${id}`, data);
  }

  async deleteAgentFunction(id: string): Promise<void> {
    return this.delete<void>(`/agent-functions/${id}`);
  }

  // ─── EXECUTION ─────────────────────────────────────────────────────────

  async executeAgentFunction(
    slug: string,
    inputJson: string,
    file?: File
  ): Promise<AgentFunctionRequestDto> {
    const formData = new FormData();
    formData.append('inputJson', inputJson);
    if (file) {
      formData.append('file', file);
    }

    return this.post<AgentFunctionRequestDto>(
      `/agent-functions/${encodeURIComponent(slug)}/execute`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  // ─── REQUEST HISTORY ───────────────────────────────────────────────────

  async getAgentFunctionRequests(
    functionId: string,
    filters?: AgentFunctionRequestFilters
  ): Promise<PagedResult<AgentFunctionRequestDto>> {
    const params = new URLSearchParams();
    if (filters?.pageNumber) params.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters?.status !== undefined) params.append('status', filters.status.toString());

    const url = `/agent-functions/${functionId}/requests${params.toString() ? `?${params.toString()}` : ''}`;
    return this.get<PagedResult<AgentFunctionRequestDto>>(url);
  }

  async getAgentFunctionRequest(requestId: string): Promise<AgentFunctionRequestDetailDto> {
    return this.get<AgentFunctionRequestDetailDto>(`/agent-functions/requests/${requestId}`);
  }
}

export const agentFunctionService = new AgentFunctionService();
